import { useState } from 'react';
import {
  DEAL_REVENUE_MODES,
  countsTowardValue,
  type DealCost,
  type DealLineItem,
  type DealMilestone,
  type DealRevenueMode,
} from '@forecastify/shared';
import {
  Badge,
  Button,
  DropdownMenu,
  Empty,
  Icon,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  cn,
} from '@forecastify/ui';
import { useI18n, useT } from '../../i18n';
import { useDuration } from '../../duration';
import { formatCalendarDate } from '../history/history-labels';
import type { DealTabProps } from './deal-tab';
import {
  dealMoney,
  lineStatusLabel,
  lineTypeLabel,
  rateUnitLabel,
  revenueModeLabel,
} from './deal-kit';
import { CostDialog, MilestoneDialog } from './plan-dialogs';
import { PlanConfirm, PlanSectionHead, moneyByCurrency } from './plan-kit';
import { LineItemDialog, lineHasWork } from './plan-line-dialog';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D6 · the record's *Terv* — DM-4.                  │
 * │ `phase6/08-deal-plan.png`, `08b-deal-plan-empty.png`, `15b`.              │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The line items (the deal's price and its estimate), the milestones they hang on, the external
 * costs, and the revenue schedule the forecast and the generator read. Every write goes through
 * `record.write`, whose answer is the whole record — the totals here and on the Erőforrás tiles
 * are the server's, recomputed, never summed twice.
 *
 * ⚠️ **An option is drawn and left out of every Σ** (`countsTowardValue`) — marked on its row and
 * summed separately in the footer, so a reader can see what taking it would add.
 *
 * ⚠️ **No toolbar search, filter or view switch** where the mockup draws one: a deal carries a
 * handful of line items, and a filter over six rows hides more than it finds.
 */

type Pending =
  | { readonly kind: 'line'; readonly id: string; readonly name: string }
  | { readonly kind: 'milestone'; readonly id: string; readonly name: string }
  | { readonly kind: 'cost'; readonly id: string; readonly name: string };

export function DealPlanTab({ deal, record, feeds, canManage }: DealTabProps) {
  const t = useT();
  const { fmt } = useDuration();
  const { locale } = useI18n();
  const defaultCurrency = feeds.config?.settings.currencyCode ?? deal.currencyCode;

  const [lineDialog, setLineDialog] = useState<{ line: DealLineItem | null } | null>(null);
  const [milestoneDialog, setMilestoneDialog] = useState<{
    milestone: DealMilestone | null;
  } | null>(null);
  const [costDialog, setCostDialog] = useState<{ cost: DealCost | null } | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [pendingError, setPendingError] = useState<string | null>(null);
  const [modeError, setModeError] = useState<string | null>(null);

  const date = (value: string | null): string =>
    value === null ? '—' : formatCalendarDate(value, locale);

  const counted = deal.lineItems.filter((line) => countsTowardValue(line.type));
  const options = deal.lineItems.filter((line) => !countsTowardValue(line.type));
  const estimateSum = counted.reduce((sum, line) => sum + Number(line.estimateHours), 0);

  const setRevenueMode = async (mode: DealRevenueMode): Promise<void> => {
    setModeError(null);
    const refusal = await record.write('', 'PATCH', { version: deal.version, revenueMode: mode });
    if (refusal !== null) setModeError(refusal);
  };

  const confirmDelete = async (): Promise<void> => {
    if (pending === null) return;
    const path =
      pending.kind === 'line'
        ? `/line-items/${pending.id}`
        : pending.kind === 'milestone'
          ? `/milestones/${pending.id}`
          : `/costs/${pending.id}`;
    const refusal = await record.write(path, 'DELETE');
    if (refusal !== null) {
      setPendingError(refusal);
      return;
    }
    setPending(null);
  };

  const rateText = (line: DealLineItem): string => {
    if (line.rate === null) return '—';
    const money = dealMoney(line.rate, line.currencyCode);
    if (line.type === 'tm') return `${money} / ${rateUnitLabel(t, line.rateUnit)}`;
    return line.quantity === null ? money : `${String(Number(line.quantity))} × ${money}`;
  };

  const rowMenu = (items: Parameters<typeof DropdownMenu>[0]['items']) => (
    <DropdownMenu label={t('list.rowMenu')} align="end" items={items}>
      <Button variant="ghost" size="icon-sm" aria-label={t('list.rowMenu')}>
        <Icon name="ellipsis" size={16} />
      </Button>
    </DropdownMenu>
  );

  return (
    <div data-screen="deal-plan" className="flex flex-col pb-10">
      {/* ── the revenue schedule and the one action ── */}
      <div className="flex flex-wrap items-center gap-3 border-b px-4 py-2.5">
        <label className="flex items-center gap-2 text-[12.5px]">
          <span className="text-muted-foreground">{t('dealPlan.revenueMode')}</span>
          <Tooltip content={t('dealPlan.revenueMode.hint')} width={260}>
            <Icon name="circle-help" size={14} className="text-muted-foreground" />
          </Tooltip>
          <Select
            className="w-[200px]"
            value={deal.revenueMode}
            disabled={!canManage}
            aria-label={t('dealPlan.revenueMode')}
            onChange={(event) => {
              void setRevenueMode(event.target.value as DealRevenueMode);
            }}
            options={DEAL_REVENUE_MODES.map((mode) => ({
              value: mode,
              label: revenueModeLabel(t, mode),
            }))}
          />
        </label>
        {modeError === null ? null : (
          <span className="text-destructive text-[12px]" role="alert">
            {modeError}
          </span>
        )}
        <div className="flex-1" />
        {canManage ? (
          <Button
            size="sm"
            onClick={() => {
              setLineDialog({ line: null });
            }}
          >
            <Icon name="plus" size={14} />
            {t('dealPlan.action.newLine')}
          </Button>
        ) : null}
      </div>

      {/* ── the line items ── */}
      <Table label={t('dealPlan.lines.label')}>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="pl-4">{t('dealPlan.column.name')}</TableHead>
            <TableHead>{t('dealPlan.column.type')}</TableHead>
            <TableHead>{t('dealPlan.column.status')}</TableHead>
            <TableHead>{t('dealPlan.column.milestone')}</TableHead>
            <TableHead className="text-right">{t('dealPlan.column.estimate')}</TableHead>
            <TableHead className="text-right">{t('dealPlan.column.allocated')}</TableHead>
            <TableHead className="text-right">{t('dealPlan.column.rate')}</TableHead>
            <TableHead className="text-right">{t('dealPlan.column.costBudget')}</TableHead>
            <TableHead className="text-right">{t('dealPlan.column.amount')}</TableHead>
            <TableHead>{t('dealPlan.column.currency')}</TableHead>
            <TableHead>{t('dealPlan.column.start')}</TableHead>
            <TableHead>{t('dealPlan.column.end')}</TableHead>
            <TableHead className="w-10" aria-label={t('list.rowMenu')} />
          </TableRow>
        </TableHeader>
        <TableBody>
          {deal.lineItems.map((line) => {
            const option = !countsTowardValue(line.type);
            const work = lineHasWork(line.type);
            const over = work && Number(line.allocatedHours) > Number(line.estimateHours);
            return (
              <TableRow
                key={line.id}
                className={cn(canManage && 'cursor-pointer')}
                onClick={
                  canManage
                    ? () => {
                        setLineDialog({ line });
                      }
                    : undefined
                }
              >
                <TableCell className="pl-4">
                  <span className="flex items-center gap-2">
                    {option ? (
                      <Tooltip content={t('dealPlan.option.hint')} width={260}>
                        <Badge variant="outline" tabIndex={0}>
                          {t('dealPlan.option.badge')}
                        </Badge>
                      </Tooltip>
                    ) : null}
                    <span className={cn('font-medium', option && 'text-muted-foreground')}>
                      {line.name}
                    </span>
                  </span>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {lineTypeLabel(t, line.type)}
                </TableCell>
                <TableCell>
                  <Badge variant={line.status === 'draft' ? 'outline' : 'secondary'}>
                    {lineStatusLabel(t, line.status)}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground">{line.milestoneName ?? '—'}</TableCell>
                <TableCell numeric>{work ? fmt(line.estimateHours) : '—'}</TableCell>
                <TableCell numeric className={cn(over && 'text-destructive')}>
                  {work ? (
                    <Tooltip asChild content={over ? t('dealPlan.overAllocated') : undefined}>
                      <span>{fmt(line.allocatedHours)}</span>
                    </Tooltip>
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell numeric className="text-muted-foreground">
                  {rateText(line)}
                </TableCell>
                <TableCell numeric>{dealMoney(line.costBudget, line.currencyCode)}</TableCell>
                <TableCell
                  numeric
                  className={cn('font-medium', option && 'text-muted-foreground font-normal')}
                >
                  {dealMoney(line.amount, line.currencyCode)}
                </TableCell>
                <TableCell className="text-muted-foreground">{line.currencyCode}</TableCell>
                <TableCell className="text-muted-foreground tabular-nums">
                  {date(line.startsOn)}
                </TableCell>
                <TableCell className="text-muted-foreground tabular-nums">
                  {date(line.endsOn)}
                </TableCell>
                <TableCell
                  className="pr-3"
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                >
                  {canManage
                    ? rowMenu([
                        {
                          label: t('common.edit'),
                          icon: 'pencil',
                          onSelect: () => {
                            setLineDialog({ line });
                          },
                        },
                        {
                          label: t('dealPlan.action.deleteLine'),
                          icon: 'trash',
                          destructive: true,
                          onSelect: () => {
                            setPendingError(null);
                            setPending({ kind: 'line', id: line.id, name: line.name });
                          },
                        },
                      ])
                    : null}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {deal.lineItems.length === 0 ? (
        <Empty
          icon="square-dashed"
          title={t('dealPlan.empty.title')}
          body={t('dealPlan.empty.body')}
          {...(canManage
            ? {
                actionLabel: t('dealPlan.action.newLine'),
                onAction: () => {
                  setLineDialog({ line: null });
                },
              }
            : {})}
        />
      ) : null}

      <div className="text-muted-foreground border-y bg-[color-mix(in_oklch,var(--muted)_40%,var(--background))] px-4 py-2.5 text-[12px] tabular-nums">
        {t('dealPlan.footer', {
          count: deal.lineItems.length,
          hours: fmt(estimateSum),
          amount: moneyByCurrency(counted, deal.currencyCode),
        })}
        {options.length === 0
          ? null
          : ` · ${t('dealPlan.footer.options', { amount: moneyByCurrency(options, deal.currencyCode) })}`}
      </div>

      {/* ── milestones and external costs, side by side ── */}
      <div className="grid gap-x-6 lg:grid-cols-2">
        <section>
          <PlanSectionHead
            title={t('dealPlan.milestones.title')}
            action={
              canManage ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setMilestoneDialog({ milestone: null });
                  }}
                >
                  <Icon name="plus" size={14} />
                  {t('dealPlan.milestones.add')}
                </Button>
              ) : undefined
            }
          />
          {deal.milestones.length === 0 ? (
            <p className="text-muted-foreground px-4 py-3 text-[12.5px]">
              {t('dealPlan.milestones.empty')}
            </p>
          ) : (
            <Table label={t('dealPlan.milestones.label')}>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="pl-4">{t('dealPlan.milestones.column.name')}</TableHead>
                  <TableHead>{t('dealPlan.milestones.column.due')}</TableHead>
                  <TableHead className="text-right">
                    {t('dealPlan.milestones.column.lines')}
                  </TableHead>
                  <TableHead className="w-10" aria-label={t('list.rowMenu')} />
                </TableRow>
              </TableHeader>
              <TableBody>
                {deal.milestones.map((milestone) => (
                  <TableRow key={milestone.id}>
                    <TableCell className="pl-4 font-medium">{milestone.name}</TableCell>
                    <TableCell className="text-muted-foreground tabular-nums">
                      {milestone.dueOn === null
                        ? t('dealPlan.milestones.noDue')
                        : formatCalendarDate(milestone.dueOn, locale)}
                    </TableCell>
                    <TableCell numeric>
                      {deal.lineItems.filter((line) => line.milestoneId === milestone.id).length}
                    </TableCell>
                    <TableCell className="pr-3">
                      {canManage
                        ? rowMenu([
                            {
                              label: t('common.edit'),
                              icon: 'pencil',
                              onSelect: () => {
                                setMilestoneDialog({ milestone });
                              },
                            },
                            {
                              label: t('dealPlan.milestones.delete'),
                              icon: 'trash',
                              destructive: true,
                              onSelect: () => {
                                setPendingError(null);
                                setPending({
                                  kind: 'milestone',
                                  id: milestone.id,
                                  name: milestone.name,
                                });
                              },
                            },
                          ])
                        : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </section>

        <section>
          <PlanSectionHead
            title={t('dealPlan.costs.title')}
            hint={t('dealPlan.costs.hint')}
            action={
              canManage ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setCostDialog({ cost: null });
                  }}
                >
                  <Icon name="plus" size={14} />
                  {t('dealPlan.costs.add')}
                </Button>
              ) : undefined
            }
          />
          {deal.costs.length === 0 ? (
            <p className="text-muted-foreground px-4 py-3 text-[12.5px]">
              {t('dealPlan.costs.empty')}
            </p>
          ) : (
            <>
              <Table label={t('dealPlan.costs.label')}>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="pl-4">{t('dealPlan.costs.column.name')}</TableHead>
                    <TableHead className="text-right">
                      {t('dealPlan.costs.column.amount')}
                    </TableHead>
                    <TableHead>{t('dealPlan.costs.column.date')}</TableHead>
                    <TableHead className="w-10" aria-label={t('list.rowMenu')} />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deal.costs.map((cost) => (
                    <TableRow key={cost.id}>
                      <TableCell className="pl-4 font-medium">{cost.name}</TableCell>
                      <TableCell numeric>{dealMoney(cost.amount, cost.currencyCode)}</TableCell>
                      <TableCell className="text-muted-foreground tabular-nums">
                        {date(cost.incurredOn)}
                      </TableCell>
                      <TableCell className="pr-3">
                        {canManage
                          ? rowMenu([
                              {
                                label: t('common.edit'),
                                icon: 'pencil',
                                onSelect: () => {
                                  setCostDialog({ cost });
                                },
                              },
                              {
                                label: t('dealPlan.costs.delete'),
                                icon: 'trash',
                                destructive: true,
                                onSelect: () => {
                                  setPendingError(null);
                                  setPending({ kind: 'cost', id: cost.id, name: cost.name });
                                },
                              },
                            ])
                          : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="text-muted-foreground border-t px-4 py-2 text-right text-[12px] tabular-nums">
                {t('dealPlan.costs.total', {
                  amount: moneyByCurrency(deal.costs, deal.currencyCode),
                })}
              </div>
            </>
          )}
        </section>
      </div>

      <LineItemDialog
        open={lineDialog !== null}
        deal={deal}
        line={lineDialog?.line ?? null}
        record={record}
        defaultCurrency={defaultCurrency}
        onClose={() => {
          setLineDialog(null);
        }}
      />
      <MilestoneDialog
        open={milestoneDialog !== null}
        milestone={milestoneDialog?.milestone ?? null}
        record={record}
        onClose={() => {
          setMilestoneDialog(null);
        }}
      />
      <CostDialog
        open={costDialog !== null}
        deal={deal}
        cost={costDialog?.cost ?? null}
        record={record}
        defaultCurrency={defaultCurrency}
        onClose={() => {
          setCostDialog(null);
        }}
      />
      <PlanConfirm
        open={pending !== null}
        title={
          pending?.kind === 'milestone'
            ? t('dealPlan.milestones.confirmTitle')
            : pending?.kind === 'cost'
              ? t('dealPlan.costs.confirmTitle')
              : t('dealPlan.confirm.lineTitle')
        }
        body={
          pending?.kind === 'milestone'
            ? t('dealPlan.milestones.confirmBody', { name: pending.name })
            : pending?.kind === 'cost'
              ? t('dealPlan.costs.confirmBody', { name: pending.name })
              : t('dealPlan.confirm.lineBody', { name: pending?.name ?? '' })
        }
        confirmLabel={t('common.delete')}
        error={pendingError}
        onCancel={() => {
          setPending(null);
        }}
        onConfirm={() => void confirmDelete()}
      />
    </div>
  );
}
